import React, { createContext, useContext, useState, useEffect } from 'react';

interface NavigationContextType {
  isNavVisible: boolean;
  showNavigation: () => void;
  hideNavigation: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isNavVisible, setIsNavVisible] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    // Listen for hero interaction event
    const handleHeroInteraction = () => {
      if (!hasInteracted) {
        setHasInteracted(true);
        setIsNavVisible(true);
      }
    };

    // Listen for scroll events
    const handleScroll = () => {
      if (!hasInteracted) {
        setHasInteracted(true);
        setIsNavVisible(true);
      }
    };

    // Listen for click events
    const handleClick = () => {
      if (!hasInteracted) {
        setHasInteracted(true);
        setIsNavVisible(true);
      }
    };

    // Listen for custom hero interaction event
    window.addEventListener('heroInteraction', handleHeroInteraction);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('click', handleClick);
    window.addEventListener('touchstart', handleClick);

    return () => {
      window.removeEventListener('heroInteraction', handleHeroInteraction);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('click', handleClick);
      window.removeEventListener('touchstart', handleClick);
    };
  }, [hasInteracted]);

  const showNavigation = () => setIsNavVisible(true);
  const hideNavigation = () => setIsNavVisible(false);

  return (
    <NavigationContext.Provider value={{ isNavVisible, showNavigation, hideNavigation }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigationVisibility = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigationVisibility must be used within a NavigationProvider');
  }
  return context;
};
